import { useState, useEffect } from "react";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { createChoiceMapping } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { ChoiceMappingDTO } from "@shared/api-types";

interface ChoiceMappingFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  mapping?: ChoiceMappingDTO;
}

const ChoiceMappingForm = ({ isOpen, onClose, onSuccess, mapping }: ChoiceMappingFormProps) => {
  const [entityName, setEntityName] = useState(mapping?.entityName || "");
  const [fieldName, setFieldName] = useState(mapping?.fieldName || "");
  const [kupValue, setKupValue] = useState(mapping?.kupValue || "");
  const [hsValue, setHsValue] = useState(mapping?.hsValue || "");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (mapping) {
      setEntityName(mapping.entityName);
      setFieldName(mapping.fieldName);
      setKupValue(mapping.kupValue);
      setHsValue(mapping.hsValue);
    }
  }, [mapping]);

  const mutation = useMutation({
    mutationFn: createChoiceMapping,
    onSuccess: () => {
      toast({
        title: "Success",
        description: mapping ? "Choice mapping updated successfully" : "Choice mapping created successfully",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/choices"] });
      queryClient.invalidateQueries({ queryKey: ["/api/field-mappings/all"] });
      onSuccess();
    },
    onError: (_error) => {
      toast({
        title: "Error",
        description: `Failed to ${mapping ? "update" : "create"} choice mapping`,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = {
      type: "choice" as const,
      entityName,
      fieldName,
      kupValue,
      hsValue,
    };

    mutation.mutate(formData);
  };

  // Sample entities and fields for demo purposes
  const entities = ["pl_title", "pl_product", "pl_author", "pl_publisher"];
  const fields = ["pl_hs_main_type", "pl_hs_format", "pl_hs_status"];

  return (
    <Dialog open={isOpen} onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-medium">{mapping ? "Edit" : "Add"} Choice Mapping</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 gap-4 items-center">
              <Label htmlFor="entityName" className="text-right">
                Entity
              </Label>
              <Select
                value={entityName}
                onValueChange={setEntityName}
                required
                name="entityName"
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select entity" />
                </SelectTrigger>
                <SelectContent>
                  {entities.map((entity) => (
                    <SelectItem key={entity} value={entity}>
                      {entity}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-4 gap-4 items-center">
              <Label htmlFor="fieldName" className="text-right">
                Field
              </Label>
              <Select
                value={fieldName}
                onValueChange={setFieldName}
                required
                name="fieldName"
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select field" />
                </SelectTrigger>
                <SelectContent>
                  {fields.map((field) => (
                    <SelectItem key={field} value={field}>
                      {field}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-4 gap-4 items-center">
              <Label htmlFor="kupValue" className="text-right">
                KUP Value
              </Label>
              <Input
                id="kupValue"
                value={kupValue}
                onChange={(e) => setKupValue(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
            
            <div className="grid grid-cols-4 gap-4 items-center">
              <Label htmlFor="hsValue" className="text-right">
                HS Value
              </Label>
              <Input
                id="hsValue"
                value={hsValue}
                onChange={(e) => setHsValue(e.target.value)}
                className="col-span-3"
                required
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#0078D4] hover:bg-[#2B88D8]"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : mapping ? "Update Mapping" : "Create Mapping"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ChoiceMappingForm;
